import { Component, createRef } from 'react';
import { withTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import moment from 'moment';

import { scrollToBlock } from '../../../helpers/scrollToBlock';

// The Search Console property list has one implementation in the app, on the tool that introduced
// it; every GSC tool picks a property out of everything Search Console exposes to the user, which
// is a wider list than the domains linked to GDDash.
import { getUserDomains } from '../../DomainPerformance/query';
import ScopeControls from '../GscTools/components/ScopeControls';
import CoverageStrip from '../GscTools/components/CoverageStrip';
import { ELIGIBLE_PERMISSIONS, probeLatestDate } from '../GscTools/query';
import { downloadCsv } from '../GscTools/csv';
import {
    buildSeries,
    coverageFor,
    hasDuplicateKeys,
    metricsFor,
    normalizeTotals,
    reportsPosition,
    sumRows,
} from '../GscTools/metrics';
import {
    DETAIL_MAX_DAYS,
    defaultPeriod,
    detailPeriod,
    formatPeriod,
    periodEndingOn,
} from '../GscTools/period';
import { readParams, writeParams } from '../GscTools/url';

import ExtraControls from './components/ExtraControls';
import GroupComparison from './components/GroupComparison';
import GroupEditor from './components/GroupEditor';
import GroupRules from './components/GroupRules';
import GroupTrend from './components/GroupTrend';
import MatchingPages from './components/MatchingPages';
import { buildComparisonCsv, buildPagesCsv } from './exportRows';
import { fetchGroupDaily, fetchGroupPages, fetchGroupTotals } from './query';
import { orderedComparisonRows, pageRows } from './rows';
import {
    URL_KEYS,
    hasReportScope,
    initialDraft,
    reportDraftFromParams,
    reportParamsFromScope,
    resolveReportScope,
} from './scope';

const IDLE_TOTALS = { status: 'idle', error: null, current: null, baseline: null };
const IDLE_TREND = {
    status: 'idle',
    error: null,
    baselineError: null,
    current: null,
    baseline: null,
};
const IDLE_PAGES = { status: 'idle', error: null, chunk: null, loadingMore: false };

const DEFAULT_METRIC = 'clicks';

/**
 * Content Groups — one Search Console property's traffic, read through named URL groups the user
 * writes for this session, compared group by group and opened one group at a time.
 *
 * The groups are not Google's. Search Console has no notion of a section of a site, so a group is
 * nothing more than the page filters its rules compile to, and every figure on the page is the
 * answer to that filtered ask: the rules are stated first, the filter each was sent as is printed
 * beside it, and the export carries them with the numbers they produced.
 *
 * Groups are never a partition of the property. Each is requested on its own, a page can meet two
 * groups' rules, and such a page is counted in both — so no figure is ever summed across groups,
 * no total row is drawn, and the matching URLs name every other group they also fall into instead
 * of being deduplicated against them. Nothing is subtracted from an unfiltered total either: a
 * group is only ever its own response.
 *
 * Requests per applied scope: one totals call per group, holding both windows, so a group that
 * fails leaves the others standing; then, for the active group only, its daily series for each
 * window as separate calls and the first chunk of its matching pages for the detail window. Picking
 * another group re-asks for those two blocks and nothing else; clearing the pick drops what they
 * were still waiting for. A session with no groups is never sent — the report refuses it rather
 * than quietly becoming a whole-property fetch.
 *
 * Two kinds of staleness are guarded separately. A scope generation is bumped by every apply and
 * refresh, so a slow answer to a scope the user has moved on from is dropped; a detail generation
 * is bumped by every pick and every apply, so one group's trend can never land under another
 * group's heading.
 *
 * Nothing fetched is persisted. The rules are session-only: they live in this page's address
 * once applied, which is the whole of their lifetime, so a reload or a shared link reproduces the
 * groups along with the figures and closing the tab leaves nothing behind.
 */
class ContentGroups extends Component {
    constructor(props) {
        super(props);

        this.handleDraftChange = this.handleDraftChange.bind(this);
        this.handleRulesChange = this.handleRulesChange.bind(this);
        this.handleRestoreRules = this.handleRestoreRules.bind(this);
        this.handleApply = this.handleApply.bind(this);
        this.handleRefresh = this.handleRefresh.bind(this);
        this.handleToggleControls = this.handleToggleControls.bind(this);
        this.handleMode = this.handleMode.bind(this);
        this.handleSelectGroup = this.handleSelectGroup.bind(this);
        this.handleTrendMetric = this.handleTrendMetric.bind(this);
        this.handleLoadMore = this.handleLoadMore.bind(this);
        this.handleExportComparison = this.handleExportComparison.bind(this);
        this.handleExportPages = this.handleExportPages.bind(this);
        this.handlePrint = this.handlePrint.bind(this);
        this.handleDownloadImage = this.handleDownloadImage.bind(this);

        this.credentialsDomainId = props.credentials_domain_id || '';
        this.reportRef = createRef();
        // The two blocks a picked group fills, so a pick can bring them into view.
        this.detailsRef = createRef();

        this.generation = 0;
        this.detailGeneration = 0;
        this.isMounted_ = false;

        const period = defaultPeriod();

        // The address is read once, here, so the controls show the report it names from the first
        // paint rather than flicking from the defaults to it a moment later.
        const params = readParams();
        this.urlDraft = reportDraftFromParams(params);
        this.urlHasScope = hasReportScope(params);

        this.state = {
            properties: [],
            loadingProperties: true,
            propertiesError: null,
            latestDate: '',

            draft: { ...initialDraft(period), ...this.urlDraft },

            applied: null,
            loading: false,
            fetchedAt: null,
            controlsOpen: true,
            capturing: false,

            totals: {},
            trend: { ...IDLE_TREND },
            pages: { ...IDLE_PAGES },
            trendMetric: DEFAULT_METRIC,
        };
    }

    async componentDidMount() {
        this.isMounted_ = true;

        let properties = [];
        let propertiesError = null;

        try {
            const sites = await getUserDomains();
            properties = sites.filter((site) => ELIGIBLE_PERMISSIONS.has(site.permissionLevel));
        } catch (err) {
            console.log(err);
            propertiesError = err?.message ?? this.props.t('global.something_went_wrong');
        }

        if (!this.isMounted_) return;

        // A link naming a property this account cannot read is not quietly answered with another
        // property's figures: the first one is selected and the scope bar is left open for the
        // user to confirm, rather than a report being run under a scope nobody asked for.
        const requested = this.urlDraft.siteUrl
            ? properties.find((item) => item.siteUrl === this.urlDraft.siteUrl)
            : null;
        const property = requested ?? properties[0];
        const autoApply = this.urlHasScope && !!property && (!this.urlDraft.siteUrl || !!requested);

        this.setState(
            (state) => ({
                properties,
                propertiesError,
                loadingProperties: false,
                draft: property
                    ? {
                          ...state.draft,
                          siteUrl: property.siteUrl,
                          permissionLevel: property.permissionLevel,
                      }
                    : state.draft,
            }),
            () => {
                // Dates that came from the address are the user's own: the probe still runs, for
                // the preset anchor, but it does not move them.
                if (property) {
                    this.suggestDates(property.siteUrl, { seedDates: !this.urlDraft.startDate });
                }
                if (autoApply) this.handleApply();
            },
        );
    }

    componentWillUnmount() {
        this.isMounted_ = false;
    }

    /**
     * Seeds the date controls from the property's most recent activity, so the window the user is
     * offered has something in it rather than ending in days Search Console has not filled yet.
     */
    async suggestDates(siteUrl, { seedDates = true } = {}) {
        if (!this.credentialsDomainId || !siteUrl) return;

        try {
            const latest = await probeLatestDate({
                credentialsDomainId: this.credentialsDomainId,
                siteUrl,
                searchType: this.state.draft.searchType,
            });

            if (!latest || !this.isMounted_) return;

            this.setState((state) => {
                if (state.draft.siteUrl !== siteUrl) return null;
                // Kept as well as applied: the date presets count back from this day, so one
                // click lands on days the property has data for.
                if (!seedDates) return { latestDate: latest };

                return { latestDate: latest, draft: { ...state.draft, ...periodEndingOn(latest) } };
            });
        } catch (err) {
            // A probe that fails costs nothing: the controls keep their default window.
            console.log(err);
        }
    }

    scope() {
        return resolveReportScope(this.state.draft, this.props.t);
    }

    /**
     * The window the matching URLs cover.
     *
     * It is the only request carrying a page dimension, which multiplies the rows retrieved per
     * day, so it is held to the shared detail bound rather than holding the whole report to it.
     * When that shortens the window the block says so.
     */
    pagesWindow(scope) {
        return detailPeriod(scope.current, DETAIL_MAX_DAYS);
    }

    /** The metric the trend draws, held to the ones this surface actually reports. */
    trendMetric() {
        const searchType = this.state.applied?.searchType ?? this.state.draft.searchType;
        return metricsFor(searchType).includes(this.state.trendMetric)
            ? this.state.trendMetric
            : DEFAULT_METRIC;
    }

    handleDraftChange(patch) {
        this.setState(
            (state) => ({ draft: { ...state.draft, ...patch } }),
            () => {
                if (patch.siteUrl) {
                    const property = this.state.properties.find(
                        (item) => item.siteUrl === patch.siteUrl,
                    );
                    this.setState(
                        (state) => ({
                            draft: {
                                ...state.draft,
                                permissionLevel: property?.permissionLevel ?? '',
                            },
                        }),
                        () => this.suggestDates(patch.siteUrl),
                    );
                }
            },
        );
    }

    handleRulesChange(rules) {
        this.setState((state) => ({ draft: { ...state.draft, rules } }));
    }

    /** Throws away an unapplied edit: the rules go back to the ones the report was read under. */
    handleRestoreRules() {
        this.setState((state) =>
            state.applied ? { draft: { ...state.draft, rules: [...state.applied.rules] } } : null,
        );
    }

    handleToggleControls() {
        this.setState((state) => ({ controlsOpen: !state.controlsOpen }));
    }

    /**
     * The trend opens on the applied metric. Only an apply moves it there — a refresh repeats the
     * same scope, and the card the user picked since is still the one they are reading.
     */
    handleApply() {
        const scope = this.scope();
        if (scope.errors.length) return;
        this.setState({ trendMetric: scope.metric });
        this.load(scope);
    }

    handleRefresh() {
        if (this.state.applied) this.load(this.state.applied);
    }

    /**
     * Re-orders the groups already retrieved.
     *
     * Applied in memory, so it never costs a request — which is also why the applied scope and the
     * address follow it: the order the table is in has to be the order the export claims.
     */
    handleMode(mode) {
        this.setState(
            (state) => ({
                draft: { ...state.draft, mode },
                applied: state.applied ? { ...state.applied, mode } : state.applied,
            }),
            () =>
                this.state.applied &&
                writeParams(reportParamsFromScope(this.state.applied), URL_KEYS),
        );
    }

    /**
     * Runs the applied scope: every group's totals at once, each landing on its own, and the
     * active group's detail beside them.
     */
    load(scope) {
        this.generation += 1;
        const generation = this.generation;

        // Written here rather than in handleApply so the address also follows a refresh, and so
        // it only ever describes a scope that was actually run.
        writeParams(reportParamsFromScope(scope), URL_KEYS);

        this.setState({
            applied: scope,
            controlsOpen: false,
            loading: true,
            fetchedAt: new Date().toISOString(),
            totals: Object.fromEntries(
                scope.groups.map((group) => [group.name, { ...IDLE_TOTALS, status: 'loading' }]),
            ),
        });

        for (const group of scope.groups) this.loadTotals(scope, group, generation);

        this.loadDetail(scope, scope.group);
    }

    /** Records one group's outcome without disturbing the others'. */
    setTotals(name, value) {
        this.setState((state) => ({ totals: { ...state.totals, [name]: value } }));
    }

    /**
     * Block 02 — one group's dimensionless totals for each window.
     *
     * `loading` is cleared as soon as the first group lands, because those are the first figures
     * on the page; the other blocks carry their own states rather than holding the scope bar's
     * spinner for everything at once.
     */
    async loadTotals(scope, group, generation) {
        const hasPosition = reportsPosition(scope.searchType);

        try {
            const { current, baseline } = await fetchGroupTotals({
                credentialsDomainId: this.credentialsDomainId,
                scope,
                group,
            });

            if (!this.isMounted_ || generation !== this.generation) return;

            this.setState({ loading: false });
            this.setTotals(group.name, {
                status: 'done',
                error: null,
                current: normalizeTotals(current, hasPosition),
                baseline: scope.baseline ? normalizeTotals(baseline, hasPosition) : null,
            });
        } catch (err) {
            console.log('totals', group.name, err);
            if (!this.isMounted_ || generation !== this.generation) return;

            this.setState({ loading: false });
            this.setTotals(group.name, {
                ...IDLE_TOTALS,
                status: 'error',
                error: err?.message ?? this.props.t('global.something_went_wrong'),
            });
        }
    }

    /**
     * Picking a group re-asks for blocks 03 and 04 under it; picking the one already open, or
     * clearing the chip, drops the pick.
     *
     * The detail generation is bumped first either way, which is what makes the previous group's
     * answers unusable the moment the pick changes — including answers still in flight when the
     * pick is cleared.
     */
    handleSelectGroup(name) {
        const scope = this.state.applied;
        if (!scope) return;

        const group = name && name !== scope.group ? name : null;
        const applied = { ...scope, group };

        writeParams(reportParamsFromScope(applied), URL_KEYS);

        this.setState(
            (state) => ({ applied, draft: { ...state.draft, group: group ?? '' } }),
            // Every pick brings the blocks into view: they sit below the fold, and a pick that
            // left the page where it was would look like nothing happened. scrollToBlock moves
            // the page only when the block is not already readable.
            () => group && scrollToBlock(this.detailsRef.current),
        );

        this.loadDetail(applied, group);
    }

    /** Blocks 03 and 04 for one group, or both back to waiting for a pick when there is none. */
    loadDetail(scope, name) {
        this.detailGeneration += 1;
        const detailGeneration = this.detailGeneration;
        const group = scope.groups.find((item) => item.name === name);

        if (!group) {
            return this.setState({ trend: { ...IDLE_TREND }, pages: { ...IDLE_PAGES } });
        }

        this.setState({
            trend: { ...IDLE_TREND, status: 'loading' },
            pages: { ...IDLE_PAGES, status: 'loading' },
        });

        this.loadTrend(scope, group, detailGeneration);
        this.loadPages(scope, group, detailGeneration, { more: false });
    }

    /**
     * Block 03 — the group's daily rows for each window, as separate calls.
     *
     * A current window that fails is the block's error; a baseline that fails only withholds the
     * comparison, and says so, while the current line is still drawn.
     */
    async loadTrend(scope, group, detailGeneration) {
        const hasPosition = reportsPosition(scope.searchType);

        const request = async (period) => {
            if (!period) return null;

            const rows = await fetchGroupDaily({
                credentialsDomainId: this.credentialsDomainId,
                scope,
                group,
                period,
            });

            return {
                series: buildSeries(rows, period, hasPosition),
                coverage: coverageFor(rows, period),
                totals: sumRows(rows, hasPosition),
            };
        };

        const [current, baseline] = await Promise.allSettled([
            request(scope.current),
            request(scope.baseline),
        ]);

        if (!this.isMounted_ || detailGeneration !== this.detailGeneration) return;

        const reason = (result) =>
            result.reason?.message ?? this.props.t('global.something_went_wrong');

        if (current.status === 'rejected') {
            console.log('trend', current.reason);
            return this.setState({
                trend: { ...IDLE_TREND, status: 'error', error: reason(current) },
            });
        }

        this.setState({
            trend: {
                status: 'done',
                error: null,
                current: current.value,
                baseline: baseline.status === 'fulfilled' ? baseline.value : null,
                baselineError: baseline.status === 'rejected' ? reason(baseline) : null,
            },
        });
    }

    /**
     * Block 04 — one chunk of the group's matching pages, appended to the ones already held when
     * loading more. A key returned twice stops retrieval rather than being counted twice.
     */
    async loadPages(scope, group, detailGeneration, { more }) {
        const previous = more ? this.state.pages.chunk : null;
        if (more && (previous?.nextOffset ?? null) === null) return;

        this.setState((state) => ({
            pages: { ...state.pages, loadingMore: more, error: null },
        }));

        try {
            const result = await fetchGroupPages({
                credentialsDomainId: this.credentialsDomainId,
                scope,
                group,
                period: this.pagesWindow(scope).period,
                offset: more ? previous.nextOffset : 0,
            });

            const rows = [...(previous?.rows ?? []), ...result.rows];
            if (hasDuplicateKeys(rows)) throw new Error(this.props.t('tools.gsc.duplicate_rows'));

            if (!this.isMounted_ || detailGeneration !== this.detailGeneration) return;

            this.setState({
                pages: {
                    status: 'done',
                    error: null,
                    loadingMore: false,
                    chunk: { ...result, rows },
                },
            });
        } catch (err) {
            console.log('pages', err);
            if (!this.isMounted_ || detailGeneration !== this.detailGeneration) return;

            this.setState((state) => ({
                pages: {
                    ...state.pages,
                    // A failure while loading more keeps the pages already retrieved; a failure
                    // on the first chunk has nothing to keep.
                    status: state.pages.chunk ? 'done' : 'error',
                    loadingMore: false,
                    error: err?.message ?? this.props.t('global.something_went_wrong'),
                },
            }));
        }
    }

    handleLoadMore() {
        const scope = this.state.applied;
        const group = scope?.groups.find((item) => item.name === scope.group);
        if (group) this.loadPages(scope, group, this.detailGeneration, { more: true });
    }

    handleTrendMetric(metric) {
        this.setState({ trendMetric: metric });
    }

    fetchedAt() {
        return this.state.fetchedAt
            ? moment(this.state.fetchedAt).format('YYYY-MM-DD HH:mm')
            : null;
    }

    exportName(prefix) {
        const scope = this.state.applied;
        return `${prefix}-${scope?.siteUrl ?? 'property'}-${scope?.current?.startDate ?? ''}`
            .replace(/[^a-z0-9_.-]+/gi, '-')
            .toLowerCase();
    }

    handleExportComparison() {
        const scope = this.state.applied;
        if (!scope) return;

        downloadCsv(
            buildComparisonCsv({
                t: this.props.t,
                scope,
                rows: orderedComparisonRows(scope, this.state.totals),
                fetchedAt: this.fetchedAt(),
            }),
            `${this.exportName('content-groups')}.csv`,
        );
    }

    handleExportPages() {
        const scope = this.state.applied;
        const chunk = this.state.pages.chunk;
        if (!scope?.group || !chunk) return;

        downloadCsv(
            buildPagesCsv({
                t: this.props.t,
                scope,
                group: scope.group,
                period: this.pagesWindow(scope).period,
                // Everything retrieved in this generation, not the page of rows on screen.
                rows: this.matchingRows(scope),
                stopReason: chunk.stopReason,
                fetchedAt: this.fetchedAt(),
            }),
            `${this.exportName(`content-group-${scope.group}`)}.csv`,
        );
    }

    handlePrint() {
        window.print();
    }

    /**
     * Captures the loaded report as an image, from the DOM already on screen — no further request
     * and no upload. The controls that produced the report and the export actions themselves are
     * left out of the frame, while the applied rules and the coverage labels stay in it, so the
     * picture still says what it is a picture of and under which groups.
     */
    async handleDownloadImage() {
        const container = this.reportRef.current;
        if (!container) return;

        this.setState({ capturing: true });

        try {
            const canvas = await html2canvas(container, {
                backgroundColor: '#ffffff',
                scale: 2,
                ignoreElements: (element) =>
                    element.classList?.contains('gsc-tool-controls') ||
                    element.classList?.contains('gsc-tool-controls-bottom') ||
                    element.classList?.contains('gsc-tool-rules') ||
                    element.classList?.contains('gsc-tool-page-actions') ||
                    // The shared chart and table carry their own refresh and download buttons.
                    element.classList?.contains('chart-action-btns') ||
                    element.classList?.contains('dt-actions__bar'),
            });

            const link = document.createElement('a');
            link.href = canvas.toDataURL('image/png');
            link.download = `${this.exportName('content-groups')}.png`;
            link.click();
        } catch (err) {
            console.log(err);
        } finally {
            if (this.isMounted_) this.setState({ capturing: false });
        }
    }

    /**
     * The coverage strip qualifies the active group's daily series, which is the only date-keyed
     * request on the page — so it says whose coverage it is, and says there is none to report
     * while no group is picked rather than claiming every requested date came back.
     */
    renderCoverage(applied) {
        const { t } = this.props;
        const { trend } = this.state;

        if (!applied.group) {
            const idle = { status: 'done', coverage: null, error: null };
            return (
                <CoverageStrip
                    scope={applied}
                    current={idle}
                    baseline={applied.baseline ? idle : null}
                    fetchedAt={this.state.fetchedAt}
                    statusNote={t('tools.content_groups.coverage.no_group')}
                />
            );
        }

        const status = trend.status === 'idle' ? 'loading' : trend.status;
        const missing =
            (trend.current?.coverage?.missing?.length ?? 0) +
            (trend.baseline?.coverage?.missing?.length ?? 0);

        return (
            <CoverageStrip
                scope={applied}
                current={{ status, coverage: trend.current?.coverage ?? null, error: trend.error }}
                baseline={
                    applied.baseline
                        ? {
                              status: trend.baselineError ? 'error' : status,
                              coverage: trend.baseline?.coverage ?? null,
                              error: trend.baselineError ?? trend.error,
                          }
                        : null
                }
                fetchedAt={this.state.fetchedAt}
                statusNote={
                    missing
                        ? t('tools.content_groups.coverage.some_missing', {
                              group: applied.group,
                              missing,
                          })
                        : t('tools.content_groups.coverage.all_present', { group: applied.group })
                }
            />
        );
    }

    /**
     * Block 04's rows, rebuilt only when what they are built from changes. Each URL is checked
     * against every other group's rules, which is worth doing once per retrieval rather than on
     * every render of the page.
     */
    matchingRows(scope) {
        const chunk = this.state.pages.chunk;
        const cache = this.matchingCache;
        if (cache && cache.chunk === chunk && cache.scope === scope) return cache.rows;

        const rows = pageRows(scope, chunk?.rows, scope.group);
        this.matchingCache = { chunk, scope, rows };
        return rows;
    }

    renderReport(applied) {
        const { t } = this.props;
        const { totals, trend, pages } = this.state;

        const pagesWindow = this.pagesWindow(applied);

        return (
            <>
                {this.renderCoverage(applied)}

                <GroupRules scope={applied} />

                <GroupComparison
                    scope={applied}
                    totals={totals}
                    rows={orderedComparisonRows(applied, totals)}
                    activeGroup={applied.group}
                    onMode={this.handleMode}
                    onSelect={this.handleSelectGroup}
                    onExport={this.handleExportComparison}
                />

                {/* The two blocks a picked group fills, wrapped so a pick can bring them into
                    view. */}
                <div ref={this.detailsRef}>
                    <GroupTrend
                        scope={applied}
                        group={applied.group}
                        status={trend.status}
                        error={trend.error}
                        baselineError={trend.baselineError}
                        current={trend.current}
                        baseline={trend.baseline}
                        metric={this.trendMetric()}
                        onMetric={this.handleTrendMetric}
                    />

                    <MatchingPages
                        scope={applied}
                        group={applied.group}
                        period={pagesWindow.period}
                        shortened={pagesWindow.shortened}
                        status={pages.status}
                        error={pages.error}
                        rows={this.matchingRows(applied)}
                        stopReason={pages.chunk?.stopReason ?? 'empty_page'}
                        loadingMore={pages.loadingMore}
                        onLoadMore={this.handleLoadMore}
                        onExport={this.handleExportPages}
                    />
                </div>

                <p className="gsc-tool-table-note">
                    {t('tools.content_groups.footnote', { period: formatPeriod(applied.current) })}
                </p>
            </>
        );
    }

    render() {
        const { t } = this.props;
        const scope = this.scope();
        const applied = this.state.applied;
        const ready = Object.values(this.state.totals).some((item) => item.status === 'done');

        return (
            <div className="gsc-tool" ref={this.reportRef}>
                <div className="gsc-tool-title-row">
                    <div>
                        <h2 className="page-title">{t('tools.content_groups.header')}</h2>
                        <p className="gsc-tool-sub">{t('tools.content_groups.subtitle')}</p>
                    </div>

                    <div className="gsc-tool-page-actions">
                        <button
                            type="button"
                            className="button button-outline button-primary btn"
                            onClick={this.handleDownloadImage}
                            disabled={!ready || this.state.capturing}
                        >
                            <i className="icon-fi_download" />
                            {this.state.capturing
                                ? t('tools.gsc.exports.capturing')
                                : t('tools.gsc.exports.image')}
                        </button>
                        <button
                            type="button"
                            className="button button-outline button-primary btn"
                            onClick={this.handlePrint}
                            disabled={!ready}
                        >
                            <i className="icon-fi_download" />
                            {t('tools.gsc.exports.full_page')}
                        </button>
                    </div>
                </div>

                {!this.credentialsDomainId && (
                    <div className="gsc-tool-banner error">
                        {t('tools.gsc.controls.no_credentials')}
                    </div>
                )}

                <ScopeControls
                    draft={this.state.draft}
                    scope={scope}
                    applied={applied}
                    open={this.state.controlsOpen}
                    properties={this.state.properties}
                    loadingProperties={this.state.loadingProperties}
                    propertiesError={this.state.propertiesError}
                    latestDate={this.state.latestDate}
                    loading={this.state.loading}
                    onChange={this.handleDraftChange}
                    onApply={this.handleApply}
                    onRefresh={this.handleRefresh}
                    onToggle={this.handleToggleControls}
                    extraScopeLabels={
                        applied
                            ? [
                                  t('tools.content_groups.controls.groups_applied', {
                                      groups: applied.groups.length,
                                  }),
                                  t(`tools.content_groups.modes.${applied.mode}`),
                              ]
                            : []
                    }
                    footer={
                        <GroupEditor
                            rules={this.state.draft.rules}
                            appliedRules={applied?.rules ?? null}
                            onChange={this.handleRulesChange}
                            onRestore={this.handleRestoreRules}
                        />
                    }
                >
                    <ExtraControls draft={this.state.draft} onChange={this.handleDraftChange} />
                </ScopeControls>

                {!applied ? (
                    <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
                        <div className="card-body gsc-tool-load-state">
                            <span className="gsc-tool-load-icon" aria-hidden="true">
                                <i className="icon-fi_grid" />
                            </span>
                            <h4>{t('tools.content_groups.idle_title')}</h4>
                            <p>{t('tools.content_groups.idle')}</p>
                        </div>
                    </div>
                ) : (
                    this.renderReport(applied)
                )}
            </div>
        );
    }
}

export default withTranslation()(ContentGroups);
